import React, { ReactNode } from 'react';
import {
    MdApartment,
    MdEdit,
    MdGroups,
    MdMailOutline,
    MdNumbers,
    MdPhone,
    MdPhotoCamera,
    MdWorkOutline,
} from 'react-icons/md';
import OnboardingHint from './OnboardingHint';
import './UserPanel.scss';

/** Сведения карточки пользователя; пустое поле — без строки. */
export interface IUserPanelUser {
    /** ФИО целиком */
    fullName: string;
    /** Должность — чипом под ФИО */
    position?: string;
    /** Организация; строка есть, если поле задано (и пустым — со сменой организации) */
    department?: string;
    /** ИНН организации */
    departmentInn?: string;
    /** Подразделение */
    subdivision?: string;
    phone?: string;
    email?: string;
}

export interface IUserPanelProps {
    user: IUserPanelUser;
    /** Аватар 64px — `Avatar` с `size={AVATAR_SIZES.xl}` (`@srm/ui/avatar`) */
    avatar: ReactNode;
    /** Сменить фото: аватар становится кнопкой «Изменить фото профиля» */
    onEditAvatar?: () => void;
    /** Сменить организацию: кнопка в строке организации */
    onChangeDepartment?: () => void;
    /** Показать онбординг-подсказку «Добавьте фото» у аватара */
    avatarHintOpen?: boolean;
    /** Закрыть подсказку у аватара крестиком */
    onDismissAvatarHint?: () => void;
}

/** Карточка пользователя в меню профиля: аватар, ФИО, должность, организация и контакты. */
const UserPanel: React.FC<IUserPanelProps> = ({
    user,
    avatar,
    onEditAvatar,
    onChangeDepartment,
    avatarHintOpen = false,
    onDismissAvatarHint,
}) => {
    return (
        <div className="user-info">
            <div className="user-info-main">
                {onEditAvatar ? (
                    <OnboardingHint
                        opened={avatarHintOpen}
                        icon={<MdPhotoCamera size={17} />}
                        title="Добавьте фото"
                        text="Нажмите на аватар, чтобы загрузить изображение"
                        onActivate={onEditAvatar}
                        onClose={onDismissAvatarHint}
                    >
                        <button
                            type="button"
                            className="user-photo user-photo--editable"
                            data-testid="user-photo"
                            onClick={onEditAvatar}
                            aria-label="Изменить фото профиля"
                        >
                            {avatar}
                            <span className="user-photo__overlay">
                                <MdPhotoCamera size={18} aria-hidden="true" />
                                <span className="user-photo__overlay-text">Изменить</span>
                            </span>
                        </button>
                    </OnboardingHint>
                ) : (
                    <div className="user-photo">{avatar}</div>
                )}

                <div className="user-info-head">
                    <div className="user-full-name" data-testid="user-full-name">
                        {user.fullName}
                    </div>
                    {user.position && (
                        <span className="user-role-chip" data-testid="user-position" title={user.position}>
                            <MdWorkOutline aria-hidden="true" />
                            <span>{user.position}</span>
                        </span>
                    )}
                </div>
            </div>

            <div className="user-info-meta">
                {user.department !== undefined && (
                    <div className="user-meta-row">
                        <MdApartment className="user-meta-row__icon" aria-hidden="true" />
                        <span
                            className="user-meta-row__value"
                            title={user.department}
                            data-testid="user-department-name"
                        >
                            {user.department}
                        </span>
                        {onChangeDepartment && (
                            <button
                                type="button"
                                className="user-meta-row__edit"
                                title="Сменить организацию"
                                aria-label="Сменить организацию"
                                onClick={onChangeDepartment}
                                data-testid="edit-user-department-button"
                            >
                                <MdEdit aria-hidden="true" />
                            </button>
                        )}
                    </div>
                )}

                {user.departmentInn && (
                    <div className="user-meta-row">
                        <MdNumbers className="user-meta-row__icon" aria-hidden="true" />
                        <span
                            className="user-meta-row__value"
                            title={`ИНН организации: ${user.departmentInn}`}
                            data-testid="user-department-inn"
                        >
                            <span className="user-meta-row__tag">ИНН</span>
                            {user.departmentInn}
                        </span>
                    </div>
                )}

                {user.subdivision && (
                    <div className="user-meta-row">
                        <MdGroups className="user-meta-row__icon" aria-hidden="true" />
                        <span className="user-meta-row__value" title={user.subdivision} data-testid="user-subdivision">
                            {user.subdivision}
                        </span>
                    </div>
                )}

                {user.phone && (
                    <div className="user-meta-row">
                        <MdPhone className="user-meta-row__icon" aria-hidden="true" />
                        <a
                            className="user-meta-row__value user-meta-row__value--link"
                            href={`tel:${user.phone}`}
                            data-testid="user-phone"
                        >
                            {user.phone}
                        </a>
                    </div>
                )}

                {user.email && (
                    <div className="user-meta-row">
                        <MdMailOutline className="user-meta-row__icon" aria-hidden="true" />
                        <a
                            className="user-meta-row__value user-meta-row__value--link"
                            href={`mailto:${user.email}`}
                            data-testid="user-email"
                        >
                            {user.email}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserPanel;
